import { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Camera, Upload, Trash2, Loader2, ImagePlus, X, Heart, MessageCircle,
  ZoomIn, ChevronLeft, ChevronRight, TrendingUp, Info
} from 'lucide-react';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';
import { useAuth } from '../context/AuthContext';

function resolveUrl(url) {
  if (!url) return '';
  return url.startsWith('http') ? url : `${API.replace('/api', '')}${url}`;
}

// Full-screen lightbox viewer
function PhotoLightboxViewer({ photos, index, onClose, onLike, getAuthHeaders, isOwn }) {
  const [current, setCurrent] = useState(index || 0);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const photo = photos[current];

  useEffect(() => {
    if (!photo) return;
    setLikeCount(photo.likes || 0);
    // Check if user liked this photo
    if (photo.source === 'upload' && getAuthHeaders) {
      fetch(`${API}/users/photos/${photo.photo_id}/likes`, { headers: getAuthHeaders() })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) { setLiked(d.user_liked); setLikeCount(d.likes); } })
        .catch(() => {});
    }
  }, [current, photo, getAuthHeaders]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && current > 0) setCurrent(c => c - 1);
      if (e.key === 'ArrowRight' && current < photos.length - 1) setCurrent(c => c + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, photos.length, onClose]);

  const handleLike = async () => {
    if (!photo || photo.source !== 'upload' || !getAuthHeaders) return;
    try {
      const res = await fetch(`${API}/users/photos/${photo.photo_id}/like`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      if (res.ok) {
        const d = await res.json();
        setLiked(d.liked);
        setLikeCount(prev => d.liked ? prev + 1 : Math.max(0, prev - 1));
        if (onLike) onLike(photo.photo_id, d.liked);
      }
    } catch {}
  };

  if (!photo) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
      data-testid="photo-lightbox-viewer"
    >
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
        data-testid="lightbox-close"
      >
        <X className="w-5 h-5 text-white" />
      </button>

      {/* Nav */}
      {current > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); setCurrent(c => c - 1); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-50 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
          data-testid="lightbox-prev"
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
      )}
      {current < photos.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); setCurrent(c => c + 1); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-50 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
          data-testid="lightbox-next"
        >
          <ChevronRight className="w-5 h-5 text-white" />
        </button>
      )}

      {/* Image */}
      <div className="max-w-[90vw] max-h-[85vh] relative" onClick={(e) => e.stopPropagation()}>
        <img
          src={resolveUrl(photo.photo_url || photo.image_url)}
          alt={photo.caption || ''}
          className="max-w-full max-h-[85vh] object-contain rounded-lg"
          data-testid="lightbox-image"
        />

        {/* Bottom bar */}
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent rounded-b-lg">
          <div className="flex items-center gap-3">
            {photo.source === 'upload' && (
              <button
                onClick={handleLike}
                className="flex items-center gap-1.5 text-white/90 hover:text-white transition-colors"
                data-testid="lightbox-like-btn"
              >
                <Heart className={`w-5 h-5 ${liked ? 'fill-red-500 text-red-500' : ''}`} />
                <span className="text-sm font-medium">{likeCount}</span>
              </button>
            )}
            {photo.source === 'post' && (
              <>
                <div className="flex items-center gap-1 text-white/70">
                  <Heart className="w-4 h-4" />
                  <span className="text-xs">{photo.likes || 0}</span>
                </div>
                <div className="flex items-center gap-1 text-white/70">
                  <MessageCircle className="w-4 h-4" />
                  <span className="text-xs">{photo.comments_count || 0}</span>
                </div>
              </>
            )}
            {photo.caption && (
              <span className="text-white/80 text-sm truncate ml-2 flex-1">{photo.caption}</span>
            )}
            <span className="text-white/40 text-xs ml-auto">
              {current + 1} / {photos.length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Listener Photo Library
export function ListenerPhotoLibrary({ userId, isOwnProfile, getAuthHeaders, profile }) {
  const { isAuthenticated } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [total, setTotal] = useState(0);
  const [uploadLimit, setUploadLimit] = useState(25);
  const [uploadCount, setUploadCount] = useState(0);
  const [remaining, setRemaining] = useState(25);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [dragOver, setDragOver] = useState(false);
  const [filter, setFilter] = useState('all'); // all, uploads, posts
  const fileInputRef = useRef(null);

  const fetchPhotos = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const headers = getAuthHeaders ? getAuthHeaders() : {};
      const res = await fetch(`${API}/users/${userId}/photos?limit=100&source=${filter}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setPhotos(data.photos || []);
        setTotal(data.total || 0);
        setUploadLimit(data.upload_limit || 25);
        setUploadCount(data.upload_count || 0);
        setRemaining(data.remaining ?? 25);
      }
    } catch {}
    setLoading(false);
  }, [userId, filter, getAuthHeaders]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const handleUpload = async (file) => {
    if (!file || !isOwnProfile) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Use JPG, PNG, or WEBP images');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Max file size is 10MB');
      return;
    }
    if (remaining <= 0) {
      toast.error(`Photo limit reached (${uploadLimit}). Level up for more!`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const headers = { ...getAuthHeaders() };
      delete headers['Content-Type'];

      const uploadRes = await fetch(`${API}/upload/image`, {
        method: 'POST', headers, body: formData,
      });
      if (!uploadRes.ok) { toast.error('Upload failed'); setUploading(false); return; }
      const { url } = await uploadRes.json();

      const photoRes = await fetch(`${API}/users/photos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ photo_url: url, caption: '' }),
      });
      if (photoRes.ok) {
        const { photo } = await photoRes.json();
        setPhotos(prev => [photo, ...prev]);
        setUploadCount(prev => prev + 1);
        setRemaining(prev => prev - 1);
        setTotal(prev => prev + 1);
        toast.success('Photo added!');
      } else {
        const err = await photoRes.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save photo');
      }
    } catch { toast.error('Upload failed'); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (photoId) => {
    try {
      const res = await fetch(`${API}/users/photos/${photoId}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      if (res.ok) {
        setPhotos(prev => prev.filter(p => p.photo_id !== photoId));
        setUploadCount(prev => prev - 1);
        setRemaining(prev => prev + 1);
        setTotal(prev => prev - 1);
        toast.success('Photo deleted');
      }
    } catch { toast.error('Failed to delete'); }
  };

  const handleLike = async (photoId) => {
    if (!isAuthenticated) { toast.info('Sign in to like photos'); return; }
    try {
      const res = await fetch(`${API}/users/photos/${photoId}/like`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      if (res.ok) {
        const d = await res.json();
        setPhotos(prev => prev.map(p =>
          p.photo_id === photoId
            ? { ...p, likes: d.liked ? (p.likes || 0) + 1 : Math.max(0, (p.likes || 0) - 1), user_liked: d.liked }
            : p
        ));
      }
    } catch {}
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  };

  // Build lightbox-ready images including profile/banner
  const allImages = [];
  if (profile?.avatar_url) {
    allImages.push({ photo_id: 'avatar', photo_url: profile.avatar_url, caption: 'Profile Photo', source: 'profile', likes: 0 });
  }
  if (profile?.banner_url) {
    allImages.push({ photo_id: 'banner', photo_url: profile.banner_url, caption: 'Banner', source: 'profile', likes: 0 });
  }
  allImages.push(...photos);

  return (
    <div data-testid="listener-photo-library" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-bold text-lg text-foreground" data-testid="photo-library-title">Photo Library</h3>
          {isOwnProfile && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {uploadCount} / {uploadLimit === 999 ? '\u221E' : uploadLimit} uploads
              {remaining > 0 && remaining < 10 && ` \u2022 ${remaining} remaining`}
              {remaining <= 0 && ' \u2022 Limit reached'}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Filter pills */}
          <div className="flex items-center gap-1 bg-muted/30 rounded-full p-0.5">
            {[['all', 'All'], ['uploads', 'Uploads'], ['posts', 'From Posts']].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                data-testid={`photo-filter-${key}`}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors
                  ${filter === key ? 'bg-violet-600/30 text-violet-300 border border-violet-500/30' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {label}
              </button>
            ))}
          </div>
          {isOwnProfile && (
            <Button
              size="sm"
              disabled={uploading || remaining <= 0}
              onClick={() => fileInputRef.current?.click()}
              className="bg-violet-600 hover:bg-violet-700 text-white h-8 gap-1.5"
              data-testid="upload-photo-btn"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              <span className="text-xs">Upload</span>
            </Button>
          )}
        </div>
      </div>

      {/* Drag & Drop Zone (owner only) */}
      {isOwnProfile && (
        <div
          data-testid="photo-dropzone"
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onClick={() => !uploading && remaining > 0 && fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all
            ${dragOver ? 'border-violet-500 bg-violet-500/10' : 'border-border/50 hover:border-border/80 bg-muted/10'}
            ${uploading ? 'pointer-events-none opacity-50' : ''}`}
        >
          {uploading ? (
            <div className="flex items-center justify-center gap-2 py-2">
              <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
              <span className="text-sm text-muted-foreground">Uploading...</span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 py-2">
              <ImagePlus className="w-5 h-5 text-muted-foreground/40" />
              <span className="text-sm text-muted-foreground">
                Drag & drop or tap to upload {'\u2022'} JPG, PNG, WEBP up to 10MB
              </span>
            </div>
          )}
        </div>
      )}

      {/* Tier upgrade nudge */}
      {isOwnProfile && remaining <= 0 && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-600/10 border border-amber-500/20 text-xs" data-testid="photo-limit-nudge">
          <TrendingUp className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <span className="text-amber-300">
            Photo limit reached! Level up your listener rank or go VIP for more uploads.
          </span>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : allImages.length === 0 ? (
        /* Empty state */
        <div className="text-center py-16" data-testid="photo-empty-state">
          <Camera className="w-14 h-14 mx-auto mb-3 text-muted-foreground/20" />
          <p className="text-base font-medium text-muted-foreground/60">No photos yet</p>
          {isOwnProfile ? (
            <p className="text-sm text-muted-foreground/40 mt-1">Upload your first photo to make your profile stand out</p>
          ) : (
            <p className="text-sm text-muted-foreground/40 mt-1">This user hasn't added any photos yet</p>
          )}
        </div>
      ) : (
        /* Photo Grid */
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3" data-testid="photo-grid">
          {allImages.map((photo, idx) => (
            <div
              key={photo.photo_id}
              className="aspect-square rounded-xl overflow-hidden cursor-pointer group relative bg-muted/20"
              onClick={() => setLightbox({ open: true, index: idx })}
              data-testid={`photo-item-${photo.photo_id}`}
            >
              <img
                src={resolveUrl(photo.photo_url || photo.image_url)}
                alt={photo.caption || ''}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />

              {/* Hover overlay with stats */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-3">
                  {(photo.likes > 0 || photo.source === 'upload') && (
                    <div className="flex items-center gap-1 text-white">
                      <Heart className="w-4 h-4" />
                      <span className="text-sm font-medium">{photo.likes || 0}</span>
                    </div>
                  )}
                  {photo.comments_count > 0 && (
                    <div className="flex items-center gap-1 text-white">
                      <MessageCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">{photo.comments_count}</span>
                    </div>
                  )}
                  {!photo.likes && !photo.comments_count && (
                    <ZoomIn className="w-5 h-5 text-white" />
                  )}
                </div>
              </div>

              {/* Source badge */}
              {photo.source === 'post' && (
                <Badge className="absolute top-1.5 left-1.5 bg-blue-600/80 text-white text-[8px] px-1.5 py-0 h-4">
                  Post
                </Badge>
              )}
              {photo.source === 'profile' && (
                <Badge className="absolute top-1.5 left-1.5 bg-muted/80 text-white text-[8px] px-1.5 py-0 h-4">
                  {photo.caption || 'Profile'}
                </Badge>
              )}

              {/* Delete button (owner + uploads only) */}
              {isOwnProfile && photo.source === 'upload' && (
                <button
                  className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-red-500/80 hover:bg-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  onClick={(e) => { e.stopPropagation(); handleDelete(photo.photo_id); }}
                  data-testid={`delete-photo-${photo.photo_id}`}
                >
                  <Trash2 className="w-3 h-3 text-white" />
                </button>
              )}

              {/* Like button overlay on mobile tap */}
              {photo.source === 'upload' && (
                <button
                  className="absolute bottom-1.5 right-1.5 w-7 h-7 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  onClick={(e) => { e.stopPropagation(); handleLike(photo.photo_id); }}
                  data-testid={`like-photo-${photo.photo_id}`}
                >
                  <Heart className={`w-3.5 h-3.5 ${photo.user_liked ? 'fill-red-500 text-red-500' : 'text-white'}`} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => { if (e.target.files[0]) handleUpload(e.target.files[0]); }}
        data-testid="photo-file-input"
      />

      {/* Lightbox */}
      {lightbox.open && (
        <PhotoLightboxViewer
          photos={allImages}
          index={lightbox.index}
          onClose={() => setLightbox({ open: false, index: 0 })}
          onLike={(photoId, liked) => {
            setPhotos(prev => prev.map(p =>
              p.photo_id === photoId
                ? { ...p, likes: liked ? (p.likes || 0) + 1 : Math.max(0, (p.likes || 0) - 1), user_liked: liked }
                : p
            ));
          }}
          getAuthHeaders={getAuthHeaders}
          isOwn={isOwnProfile}
        />
      )}
    </div>
  );
}
